import { Link } from "react-router-dom";
import {
  Sparkles,
  ScanLine,
  Palette,
  Gauge,
  Users,
  ArrowRight,
  Globe,
  Shield,
  Zap,
  CheckCircle2,
} from "lucide-react";
import Logo from "@/components/Logo";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";

const HERO_BG =
  "https://images.pexels.com/photos/30175347/pexels-photo-30175347.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEACHER_IMG =
  "https://images.pexels.com/photos/5212675/pexels-photo-5212675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/* TeeBee Coin pricing — mirrors backend/routes/coins_routes.RECHARGE_PACKS.
   Kept in sync manually; if backend pricing changes, update both. */
const PRICING_PACKS = [
  { id: "starter",   name: "Starter",   coins: 25,   priceInr: 99,
    tag: "First top-up", perks: ["~1 detailed lesson plan", "5 worksheets", "Try premium features"] },
  { id: "plus",      name: "Plus",      coins: 75,   priceInr: 249,
    tag: "Light premium", perks: ["~3 detailed lesson plans", "1 Full Teaching Kit", "Cover letter + AI resume"] },
  { id: "pro",       name: "Pro",       coins: 200,  priceInr: 599, popular: true,
    tag: "Active teacher", perks: ["~13 Full Teaching Kits", "Unlimited basic features", "Best value per coin"] },
  { id: "power",     name: "Power",     coins: 500,  priceInr: 1299,
    tag: "Heavy creator", perks: ["~33 Full Teaching Kits", "Auto-Differentiated packs", "Career Copilot deep use"] },
  { id: "school",    name: "School",    coins: 1500, priceInr: 3499,
    tag: "Department", perks: ["Whole-team budget", "Bulk lesson creation", "GST invoice"] },
  { id: "institute", name: "Institute", coins: 5000, priceInr: 4999,
    tag: "Bulk · best value", perks: ["Institution-wide pool", "Lowest per-coin rate", "Custom invoicing"] },
];

const FEATURES = [
  {
    icon: Sparkles,
    title: "AI Teaching Assistant",
    desc: "Lesson plans, MCQs, worksheets, rubrics & homework — generated in seconds, aligned to your curriculum.",
    color: "tb-chip-lavender",
  },
  {
    icon: ScanLine,
    title: "Document Intelligence",
    desc: "OCR for handwritten notes, whiteboard photos, scanned PDFs, math & multilingual content.",
    color: "tb-chip-mint",
  },
  {
    icon: Palette,
    title: "Content Studio",
    desc: "Beautiful notes, presentations, study guides & posters generated for your class.",
    color: "tb-chip-butter",
  },
  {
    icon: Gauge,
    title: "AI Assessment Engine",
    desc: "Auto-evaluate descriptive answers with rubric-based scoring and personalized feedback.",
    color: "tb-chip-lavender",
  },
  {
    icon: Users,
    title: "Smart Attendance",
    desc: "QR-based attendance with instant analytics and class-level insights.",
    color: "tb-chip-mint",
  },
  {
    icon: Globe,
    title: "11+ Languages",
    desc: "English, Hindi, Kannada, Tamil, Telugu, Marathi, Bengali, Arabic, French, Spanish & more.",
    color: "tb-chip-butter",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Top nav */}
      <nav className="sticky top-0 z-30 border-b border-border bg-background/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo size="lg" />
          <div className="hidden md:flex items-center gap-7 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition">Features</a>
            <a href="#how" className="text-muted-foreground hover:text-foreground transition">How it works</a>
            <Link to="/marketplace" className="text-muted-foreground hover:text-foreground transition">Marketplace</Link>
            <Link to="/about" className="text-muted-foreground hover:text-foreground transition">About</Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-signin">Sign in</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="nav-getstarted" className="rounded-full">Get started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 tb-grid-bg pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50/70 px-3 py-1 text-xs font-medium text-violet-700 backdrop-blur dark:bg-violet-500/10 dark:text-violet-200 dark:border-violet-500/30">
                <Sparkles className="h-3.5 w-3.5" /> The AI Global OS & Marketplace for Educators
              </div>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-[3.25rem] 2xl:text-6xl tracking-tight leading-[1.08] font-medium break-words">
                The AI co-teacher that{" "}
                <span className="bg-gradient-to-r from-violet-600 via-emerald-500 to-amber-400 bg-clip-text text-transparent">
                  gives you back your evenings.
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                TeeBee plans your lessons, writes your worksheets, scans your notebooks, grades your
                assessments, runs your classroom — and now connects you to schools hiring globally.
                <span className="text-foreground font-medium"> One AI Global OS for educators.</span>
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/signup">
                  <Button size="lg" className="rounded-full px-7 h-12" data-testid="hero-cta-primary">
                    Start free <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button size="lg" variant="outline" className="rounded-full px-7 h-12" data-testid="hero-cta-marketplace">
                    Educator Marketplace
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> AI Teacher Companion</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Educator Marketplace</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Hiring Network for Schools</span>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end items-center relative order-first lg:order-last mb-4 lg:mb-0">
              <div className="absolute -inset-8 bg-gradient-to-br from-violet-200/30 via-amber-100/20 to-emerald-100/30 dark:from-violet-500/15 dark:via-amber-500/10 dark:to-emerald-500/10 blur-3xl rounded-full pointer-events-none" />

              {/* LIGHT mode — full vertical lockup PNG renders beautifully on light backgrounds */}
              <span className="relative dark:hidden inline-block w-full max-w-[360px] sm:max-w-[460px] lg:max-w-[520px] xl:max-w-[600px]">
                <img
                  src="/teebee-logo-horizontal.png?v=11"
                  alt="TeeBee — Global AI Powered OS & Marketplace for Educators · by Factskoot"
                  loading="eager"
                  className="relative w-full h-auto pointer-events-none select-none tb-logo-art"
                  data-testid="hero-brand-art"
                />
                <span
                  aria-hidden
                  className="tb-logo-tm absolute select-none pointer-events-none"
                  style={{ top: "60%", left: "80%" }}
                  data-testid="hero-tm"
                >™</span>
              </span>

              {/* DARK mode — clean icon + HTML wordmark for crisp contrast on dark backdrops */}
              <div className="relative hidden dark:flex flex-col items-center text-center max-w-md" data-testid="hero-brand-dark">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-amber-400/30 blur-3xl pointer-events-none" />
                  <span className="absolute inset-0 rounded-full bg-violet-400/25 blur-2xl pointer-events-none" />
                  <img
                    src="/icon-512.png?v=11"
                    alt="TeeBee icon"
                    className="relative h-40 w-40 sm:h-48 sm:w-48 lg:h-56 lg:w-56 object-contain tb-logo-art"
                    draggable={false}
                  />
                </div>
                <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-medium leading-none">
                  <span className="text-white">Tee</span>
                  <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">Bee</span>
                  <span
                    aria-hidden
                    className="tb-logo-tm tb-logo-tm-sm relative inline-block align-super -top-3"
                    style={{ marginLeft: "2px" }}
                    data-testid="hero-tm"
                  >™</span>
                </h2>
                <p className="mt-2 text-[11px] uppercase tracking-[0.32em] text-white/50 font-medium">by Factskoot</p>
                <p className="mt-3 text-sm sm:text-base text-white/85 font-medium">
                  Global AI Powered OS & Marketplace for Educators
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Everything a teacher needs</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium">
            One app. Your entire teaching workflow.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Stop juggling 12 tools. TeeBee replaces lesson-plan generators, OCR scanners,
            quiz makers, grading software and attendance apps with a single AI-native workspace.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            const span =
              i === 0 ? "md:col-span-4 lg:col-span-7" :
              i === 1 ? "md:col-span-2 lg:col-span-5" :
              i === 2 ? "md:col-span-3 lg:col-span-4" :
              i === 3 ? "md:col-span-3 lg:col-span-4" :
              i === 4 ? "md:col-span-3 lg:col-span-4" :
                        "md:col-span-3 lg:col-span-12";
            return (
              <div
                key={f.title}
                className={`tb-card p-7 ${span} group`}
                data-testid={`feature-card-${i}`}
              >
                <div className={`inline-flex items-center justify-center h-11 w-11 rounded-2xl ${f.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl font-medium tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Showcase */}
      <section id="how" className="bg-muted/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Built for educators</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium">
              From lesson idea to classroom-ready in 30 seconds.
            </h2>
            <ul className="mt-8 space-y-4 text-sm">
              {[
                "Type a topic — get a full lesson plan, worksheet & rubric.",
                "Scan a notebook — get editable text, summary & Q&A.",
                "Paste a student answer — get instant rubric-based grading.",
                "Open a class — track attendance, see analytics, share insights.",
              ].map((t, idx) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-200 grid place-items-center text-[11px] font-semibold">
                    {idx + 1}
                  </span>
                  <span className="text-foreground/80 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/signup">
                <Button size="lg" className="rounded-full" data-testid="how-cta">
                  Try it free <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="order-1 lg:order-2 relative">
            <div className="absolute -inset-6 tb-ai-orb opacity-30 blur-3xl rounded-[3rem]" />
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-xl">
              <img src={TEACHER_IMG} alt="Teacher in classroom" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Enterprise-grade security", desc: "GDPR, FERPA & DPDP-ready architecture with end-to-end encryption." },
            { icon: Zap, title: "Lightning-fast AI", desc: "TeeBee Intelligence multimodal pipeline tuned for teachers — sub-second drafts." },
            { icon: Globe, title: "Global from day one", desc: "Multilingual UI & content. Built in India, designed for the world." },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="tb-card p-6">
                <Icon className="h-5 w-5 text-violet-600" />
                <h4 className="mt-4 font-display font-medium">{b.title}</h4>
                <p className="mt-1.5 text-sm text-muted-foreground">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────
            PRICING — Phase 10c-C: Razorpay-review friendly. All 6 packs
            displayed with INR price, per-coin value, popular badge on Pro,
            and a clear payment / legal disclosure block underneath.
            Mocked checkout until production Razorpay keys are wired.
          ──────────────────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 pb-14">
        <div className="text-center mb-10">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-violet-700 dark:text-violet-300">
            TeeBee Coins · Pricing
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium">
            Free to start. Pay only for what you need.
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Every educator gets <strong className="text-foreground">50 free TeeBee Coins</strong> on signup —
            enough for ~2 lesson plans + 5 MCQ sets. Top up anytime; coins never expire.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-3 overflow-visible" data-testid="landing-plans-grid">
          {PRICING_PACKS.map((p) => {
            const total = p.coins + (p.bonus || 0);
            const perCoin = (p.priceInr / total).toFixed(2);
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl border p-6 transition-all bg-card ${
                  p.popular
                    ? "border-violet-400/70 dark:border-violet-400/40 shadow-[0_18px_60px_-25px_rgba(139,92,246,0.45)] tb-tracing-border"
                    : "border-border hover:border-violet-300/60"
                }`}
                data-testid={`landing-plan-${p.id}`}
              >
                {p.popular && (
                  <span className="absolute -top-2 right-5 tb-chip-lavender text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full px-2.5 py-0.5">
                    Most popular
                  </span>
                )}
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">{p.name}</p>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <p className="font-display text-3xl tracking-tight font-medium">{p.coins.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">coins</p>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{p.tag}</p>
                <p className="mt-4 font-display text-2xl tracking-tight font-medium">
                  ₹{p.priceInr.toLocaleString()}
                  <span className="ml-2 text-[11px] text-muted-foreground font-normal">· ₹{perCoin}/coin</span>
                </p>
                <Link to="/signup">
                  <Button
                    variant={p.popular ? "default" : "outline"}
                    className="mt-5 w-full rounded-full"
                    data-testid={`landing-plan-cta-${p.id}`}
                  >
                    Get started free
                  </Button>
                </Link>
                <ul className="mt-4 space-y-1.5 text-[12px] text-muted-foreground">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-1.5">
                      <span className="text-violet-600 dark:text-violet-300 mt-0.5">✦</span>
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Razorpay-required disclosure block + legal links */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-5 max-w-3xl mx-auto" data-testid="landing-payment-disclosure">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground text-center">
            Payment terms · INR · Razorpay
          </p>
          <p className="mt-3 text-[12px] text-muted-foreground text-center leading-relaxed">
            All payments are processed securely through <strong className="text-foreground">Razorpay</strong> in <strong className="text-foreground">Indian Rupees (INR)</strong>. TeeBee Coins are internal platform credits only — not crypto, not cash, not investment, not withdrawable. Used coins are generally non-refundable. Failed or duplicate payments may be reviewed.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px]">
            <Link to="/legal/terms" className="text-foreground/80 hover:text-foreground underline-offset-2 hover:underline">Terms</Link>
            <Link to="/legal/refund-policy" className="text-foreground/80 hover:text-foreground underline-offset-2 hover:underline">Refund Policy</Link>
            <Link to="/legal/pricing-billing" className="text-foreground/80 hover:text-foreground underline-offset-2 hover:underline">Pricing &amp; Billing</Link>
            <Link to="/legal/coins-policy" className="text-foreground/80 hover:text-foreground underline-offset-2 hover:underline">TeeBee Coins Policy</Link>
            <Link to="/legal/shipping-delivery" className="text-foreground/80 hover:text-foreground underline-offset-2 hover:underline">Delivery</Link>
          </div>
        </div>
      </section>

      {/* CTA — kept after pricing for momentum */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-violet-50 via-white to-emerald-50 dark:from-violet-500/10 dark:via-card dark:to-emerald-500/10 p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">
              Reclaim 12 hours a week. Starting today.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Free for individual teachers during beta. Institution onboarding available on request.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-8 h-12" data-testid="footer-cta">
                  Get started free with 50 coins
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full px-8 h-12">
                  I already have an account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
